import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AgentDeleteModal from "../../../../../components/dashboardcomponents/People/Agents/AgentDeleteModal";
import AgentSuspendModal from "../../../../../components/dashboardcomponents/People/Agents/AgentSuspendModal";
import AgentDetailHeaderBand from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/AgentDetailHeaderBand";
import AgentDetailInfoSection from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/AgentDetailInfoSection";
import AgentDetailChatSettingsSection from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/AgentDetailChatSettingsSection";
import AgentDetailGroupsTeamSection from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/AgentDetailGroupsTeamSection";
import AgentDetailKbSection from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/AgentDetailKbSection";
import { buildAgentDetailView } from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/agentDetailHelpers";
import { AgentDetailPageSkeleton } from "../../../../../components/dashboardcomponents/People/Agents/AgentLoadingSkeletons";
import {
  AGENT_DETAIL_DELETE,
  AGENT_DETAIL_SUSPEND,
} from "../../../../../components/dashboardcomponents/People/Agents/agentEvents";
import useAgentPresenceSocket from "../../../../../hooks/useAgentPresenceSocket";
import { logoutAgent } from "../../../../../store/features/logoutAgent";
import { logoutAll } from "../../../../../store/features/LogoutAll";
import {
  clearAgentDetail,
  deleteAgent,
  fetchAgentById,
  suspendAgent,
} from "../../../../../store/features/agentsApi";

export default function AgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const { detail, detailLoading, mutationLoading } = useSelector((state) => state.peopleAgents);
  const currentUser = useSelector((state) => state.loginToken?.response?.user);
  const currentUserId = currentUser?.id || currentUser?._id || null;
  const socketRef = useAgentPresenceSocket();

  useEffect(() => {
    if (agentId) dispatch(fetchAgentById({ agentId }));
    return () => dispatch(clearAgentDetail());
  }, [agentId, dispatch]);

  const agent = useMemo(() => {
    if (!detail || !agentId) return null;
    const detailId = detail.id || detail._id;
    if (String(detailId) !== String(agentId)) return null;
    return buildAgentDetailView(detail);
  }, [detail, agentId]);

  useEffect(() => {
    const onSuspend = () => setSuspendOpen(true);
    const onDelete = () => setDeleteOpen(true);
    window.addEventListener(AGENT_DETAIL_SUSPEND, onSuspend);
    window.addEventListener(AGENT_DETAIL_DELETE, onDelete);
    return () => {
      window.removeEventListener(AGENT_DETAIL_SUSPEND, onSuspend);
      window.removeEventListener(AGENT_DETAIL_DELETE, onDelete);
    };
  }, []);

  if (detailLoading && !agent) return <AgentDetailPageSkeleton />;

  if (!agent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>Agent not found.</Typography>
      </Box>
    );
  }

  const handleDelete = async () => {
    const isSelf = String(agentId) === String(currentUserId);

    // Force-logout logged-in agent before delete (same API as People Agents logout)
    if (agent.presence !== "offline" && agent.presence !== "invitationSent") {
      const logoutResult = await dispatch(
        logoutAgent({
          requestHeaders: { agentId, adminId: currentUserId },
        })
      );
      if (logoutResult.payload?.status === 200) {
        socketRef.current?.emit("logout-agent", agentId);
      }
    }

    const result = await dispatch(deleteAgent({ agentId }));
    if (deleteAgent.fulfilled.match(result)) {
      toast.success(result.payload?.message || "User deleted successfully");
      if (isSelf) {
        dispatch(logoutAll());
      } else {
        navigate("/people/agents");
      }
    } else {
      toast.error(result.payload?.message || "Failed to delete agent");
    }
  };

  const handleSuspend = async () => {
    const result = await dispatch(suspendAgent({ agentId }));
    if (suspendAgent.fulfilled.match(result)) {
      socketRef.current?.emit("logout-agent", agentId);
      toast.success("Agent suspended");
      setSuspendOpen(false);
      navigate("/people/agents?tab=suspended");
    } else {
      toast.error(result.payload?.message || "Failed to suspend agent");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#FFF",
      }}
    >
      <AgentDetailHeaderBand agent={agent} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pt: "32px",
          px: 3,
          pb: 3,
        }}
      >
        <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
          <AgentDetailInfoSection agent={agent} />
          <AgentDetailChatSettingsSection agent={agent} />
          <AgentDetailGroupsTeamSection agent={agent} />
          <AgentDetailKbSection kbPermissions={agent.kbPermissions} />
        </Box>
      </Box>

      <AgentDeleteModal
        open={deleteOpen}
        agentName={agent.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={mutationLoading}
      />
      <AgentSuspendModal
        open={suspendOpen}
        agentName={agent.name}
        onClose={() => setSuspendOpen(false)}
        onConfirm={handleSuspend}
        loading={mutationLoading}
      />
    </Box>
  );
}
